import { useEffect, useState, useCallback, useRef } from "react";
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';
import { saveSelectedSite } from '../data/global';
import { useNavigate, useLocation } from "react-router-dom";
import Bin3d from "./Bin3d";
import { createApiEndpoint } from '../index.js';
import {
    WindowChrome,
    SiteSelectPhase,
    SITE_WINDOW_IDS,
    withScrollHintMenuProps,
} from "./windowChrome/index.js";
import { cableNumberFromCableRecord } from "./siteLayoutStice.js";

const cableNumberLabel = (layoutCable) => {
    const n = cableNumberFromCableRecord({
        name: layoutCable?.cableName,
        cableName: layoutCable?.cableName,
    });
    if (n != null) return String(n);
    return layoutCable?.cableName || `Cable ${layoutCable?.cableId ?? ""}`;
};

const firstCatalogBinId = (site) => {
    const bins = site?.bins || [];
    const first = bins.find((b) => b.binDataId != null);
    return first?.binDataId != null ? String(first.binDataId) : "";
};

const findSiteInfo = (siteGuid) =>
    (global.gw.siteInfos || []).find(
        (s) =>
            global.normalizeGuidForCompare(s.siteGuid) ===
            global.normalizeGuidForCompare(siteGuid),
    );

const mapFetchUserSitesToSiteInfos = (sites) =>
    (sites || []).map((_, siteIndex) => ({
        siteIndex,
        siteGuid: _.siteGuid,
        siteName: _.main?.siteName ?? "Site",
        bins: _.bins,
        isHms: _.isHms,
        currentTemperature: _.currentTemperature,
        currentHumidity: _.currentHumidity,
        currentPressure: _.currentPressue,
        lastScanDate: _.lastScan ? new Date(String(_.lastScan).split(".")[0]) : null,
        lastScan: _.lastScan
            ? new Date(_.lastScan).toLocaleString("en-us", global.dateFormat).replaceAll("/", "-").replace(",", "")
            : "",
        taskSlots: Array.isArray(_.taskSlots) ? _.taskSlots : [],
        siteWeatherStationReadingSettings: [],
        siteWeatherStationReadings: (global.gw.currentWeatherReadings || []).filter(
            (x) => x.siteId === _.siteGuid,
        ),
    }));

const ensureSiteInfosLoaded = async () => {
    if (global.gw.siteInfos?.length > 0) return true;
    try {
        const response = await createApiEndpoint().fetchUserSites("compressed");
        const sites = response?.data?.fetchUserAttribute?.sites;
        if (!sites?.length) return false;
        global.gw.siteInfos = mapFetchUserSitesToSiteInfos(sites);
        return true;
    } catch (error) {
        console.error("[BinWindow] Failed to load site list", error);
        return false;
    }
};

const BinWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const switchInFlightRef = useRef(false);
    const bootHandledRef = useRef(false);
    const bootCompleteRef = useRef(false);
    // Set immediately (synchronously) so siteView/highlightStice never apply site camera when Bin3D mounts
    if (global.gw.selectedSite) {
        global.gw.selectedSite.isBinWindowView = true;
    }
    const [phase, setPhase] = useState("boot");
    const [pickerSiteGuid, setPickerSiteGuid] = useState("");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [binViewKey, setBinViewKey] = useState(0);
    const [isLoading, setLoading] = useState(true);
    const [selectedBinId, setSelectedBinId] = useState("");
    const [selectedCableId, setSelectedCableId] = useState("select-cable"); // "select-cable" shows "Select Cable" as default
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [binGrades, setBinGrades] = useState(new Map());
    const [siteGrade, setSiteGrade] = useState(null);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        // Also check on mount to ensure theme is applied
        if (global.gw.theme === 'light') {
            document.body.classList.add('light-mode');
        } else {
            document.body.classList.remove('light-mode');
        }
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    // Fetch bin grades and site grade for non-HMS sites (after site is initialized)
    useEffect(() => {
        if (!global.gw.selectedSite?.isSiteInitialized || !global.gw.selectedSite?.siteGuid || global.gw.selectedSite.isHms) return;
        const fetchGrades = async () => {
            try {
                const [binGradesResponse, siteGradeResponse, cableGradesResponse] = await Promise.all([
                    createApiEndpoint().fetchBinGrades("direct", { date: null }),
                    createApiEndpoint().fetchSiteGrade("direct", { date: null }, global.gw.selectedSite.siteGuid),
                    createApiEndpoint().fetchCableGrades("direct", { date: null }),
                ]);
                const gradesMap = new Map();
                if (binGradesResponse?.data?.getBinGrades) {
                    const arrayBuffer = global.base64StringToArrayBuffer(binGradesResponse.data.getBinGrades);
                    const decompressed = await global.decompress(arrayBuffer, "gzip");
                    const responseData = JSON.parse(decompressed);
                    if (responseData && Array.isArray(responseData)) {
                        responseData.forEach(bg => {
                            const binId = typeof bg.BinId === 'number' ? bg.BinId : parseInt(bg.BinId, 10);
                            if (!isNaN(binId)) {
                                const dateRead = bg.DateRead ?? bg.dateRead ?? null;
                                gradesMap.set(binId, { grade: bg.Grade, averageTimeToRead: bg.AverageTimeToRead, dateRead });
                            }
                        });
                    }
                    setBinGrades(gradesMap);
                    global.gw.selectedSite.binGrades = gradesMap;
                }
                if (siteGradeResponse?.data?.getSiteGrade) {
                    const arrayBuffer = global.base64StringToArrayBuffer(siteGradeResponse.data.getSiteGrade);
                    const decompressed = await global.decompress(arrayBuffer, "gzip");
                    const responseData = JSON.parse(decompressed);
                    if (responseData && typeof responseData.Grade === 'number') {
                        setSiteGrade(responseData.Grade);
                        global.gw.selectedSite.siteGrade = responseData.Grade;
                    }
                }
                if (cableGradesResponse?.data?.getCableGrades) {
                    const arrayBuffer = global.base64StringToArrayBuffer(cableGradesResponse.data.getCableGrades);
                    const decompressed = await global.decompress(arrayBuffer, "gzip");
                    const responseData = JSON.parse(decompressed);
                    const gradesMap = new Map();
                    if (responseData && Array.isArray(responseData)) {
                        responseData.forEach(cg => {
                            const binId = typeof cg.BinId === 'number' ? cg.BinId : parseInt(cg.BinId, 10);
                            const cableId = typeof cg.CableId === 'number' ? cg.CableId : parseInt(cg.CableId, 10);
                            if (!isNaN(binId) && !isNaN(cableId)) {
                                const dateRead = cg.DateRead ?? cg.dateRead ?? null;
                                gradesMap.set(`${binId}_${cableId}`, { grade: cg.Grade, averageTimeToRead: cg.AverageTimeToRead, dateRead });
                            }
                        });
                    }
                    global.gw.selectedSite.cableGrades = gradesMap;
                    setForceUpdate(prev => prev + 1);
                }
            } catch (err) {
                console.error('Error fetching grades for BinWindow:', err);
            }
        };
        fetchGrades();
    }, [global.gw.selectedSite?.siteGuid, global.gw.selectedSite?.isSiteInitialized]);

    const assignSelectedSiteFromInfo = useCallback((info) => {
        if (global.gw.selectedSite?.refreshId) {
            clearTimeout(global.gw.selectedSite.refreshId);
        }
        global.gw.selectedSite = new global.Site();
        global.gw.selectedSite.siteIndex = info.siteIndex;
        global.gw.selectedSite.siteGuid = info.siteGuid;
        global.gw.selectedSite.siteName = info.siteName;
        global.gw.selectedSite.lastScan = info.lastScan;
        global.gw.selectedSite.isSiteInitialized = false;
        global.gw.selectedSite.isBinWindowView = true;
        saveSelectedSite();
    }, []);

    const applySiteSelection = useCallback(async (siteGuid, { keepSelectedBin = false } = {}) => {
        if (!siteGuid || switchInFlightRef.current) return false;
        let info = findSiteInfo(siteGuid);
        if (
            !info
            && global.normalizeGuidForCompare(global.gw.selectedSite?.siteGuid) ===
                global.normalizeGuidForCompare(siteGuid)
        ) {
            info = {
                siteIndex: global.gw.selectedSite.siteIndex,
                siteGuid: global.gw.selectedSite.siteGuid,
                siteName: global.gw.selectedSite.siteName,
                lastScan: global.gw.selectedSite.lastScan,
            };
        }
        if (!info) return false;

        switchInFlightRef.current = true;
        setLoading(true);
        setIsHistoryLoading(true);
        setSelectedCableId("select-cable");
        setBinGrades(new Map());
        setSiteGrade(null);

        if (global.gw.weatherStationAbortController) {
            global.gw.weatherStationAbortController.abort();
            global.gw.weatherStationAbortController = null;
        }
        if (global.gw.dashboardInterval) {
            clearInterval(global.gw.dashboardInterval);
            global.gw.dashboardInterval = null;
        }

        try {
            const sameSite =
                global.normalizeGuidForCompare(global.gw.selectedSite?.siteGuid) ===
                global.normalizeGuidForCompare(info.siteGuid);

            if (!sameSite || !global.gw.selectedSite?.isSiteInitialized) {
                if (!sameSite || !global.gw.selectedSite?.siteGuid) {
                    assignSelectedSiteFromInfo(info);
                }
                if (!global.gw.selectedSite.isSiteInitialized) {
                    await global.initializeCustomerSite(navigate);
                }
            }

            if (global.gw.selectedSite) {
                global.gw.selectedSite.isBinWindowView = true;
            }
            global.prepareBinViewSceneMount?.();

            const bins = global.gw.selectedSite?.bins || [];
            const catalogIds = bins
                .map((b) => (b.binDataId != null ? String(b.binDataId) : null))
                .filter(Boolean);
            let nextId = "";
            if (keepSelectedBin) {
                const preferred = global.gw.selectedSite?.selectedBinId;
                const preferredStr =
                    preferred != null && preferred !== 0 && preferred !== "0"
                        ? String(preferred)
                        : "";
                if (catalogIds.includes(preferredStr)) nextId = preferredStr;
                if (!nextId && preferredStr) {
                    const byRow = bins.find(
                        (b) =>
                            String(b.binDataId) === preferredStr ||
                            String(b.binId) === preferredStr
                    );
                    if (byRow?.binDataId != null) nextId = String(byRow.binDataId);
                }
                if (!nextId && global.gw.selectedSite?.selectedBinName) {
                    const byName = bins.find(
                        (b) => b.binName === global.gw.selectedSite.selectedBinName
                    );
                    if (byName?.binDataId != null) nextId = String(byName.binDataId);
                }
            }
            if (!nextId) nextId = catalogIds[0] || firstCatalogBinId(global.gw.selectedSite);

            if (nextId && global.gw.selectedSite) {
                global.gw.selectedSite.selectedBinId = nextId;
                const bin = bins.find((b) => String(b.binDataId) === nextId);
                global.gw.selectedSite.selectedBinName = bin?.binName || "";
                global.focusBinViewOnBin?.(nextId);
                await global.prefetchBinSavedView?.(nextId);
            }
            setSelectedBinId(nextId);

            global.initializeHistory((loading) => {
                setIsHistoryLoading(loading);
            }, navigate);

            setLoadedSiteGuid(info.siteGuid);
            setPickerSiteGuid(info.siteGuid);
            setBinViewKey((k) => k + 1);
            setPhase("view");
            return true;
        } catch (error) {
            console.error("[BinWindow] Site load failed", error);
            if (global.gw.selectedSite) {
                global.gw.selectedSite.initializationError = true;
            }
            setPhase("select");
            return false;
        } finally {
            setLoading(false);
            switchInFlightRef.current = false;
        }
    }, [assignSelectedSiteFromInfo, navigate]);

    useEffect(() => {
        if (bootHandledRef.current) return;
        bootHandledRef.current = true;

        (async () => {
            setLoading(true);
            await ensureSiteInfosLoaded();

            if (location.state?.openSitePicker === true) {
                navigate(location.pathname, { replace: true, state: {} });
                setPhase("select");
                setLoading(false);
                bootCompleteRef.current = true;
                return;
            }

            const autoLoadGuid =
                location.state?.autoLoad && location.state?.siteGuid
                    ? location.state.siteGuid
                    : null;
            const keepFromBinClick = !autoLoadGuid && !location.state?.openSitePicker;
            const guidToLoad = autoLoadGuid || global.gw.selectedSite?.siteGuid || null;

            if (location.state?.autoLoad || location.state?.openSitePicker) {
                navigate(location.pathname, { replace: true, state: {} });
            }

            if (!guidToLoad) {
                setPhase("select");
                setLoading(false);
                bootCompleteRef.current = true;
                return;
            }

            const ok = await applySiteSelection(guidToLoad, { keepSelectedBin: keepFromBinClick });
            if (!ok) {
                setPhase("select");
                setLoading(false);
            }
            bootCompleteRef.current = true;
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once boot
    }, []);

    useEffect(() => {
        if (!bootCompleteRef.current) return;
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (bootHandledRef.current && siteGuid === loadedSiteGuid && phase === "view") {
            navigate(location.pathname, { replace: true, state: {} });
            return;
        }
        applySiteSelection(siteGuid, { keepSelectedBin: false }).then(() => {
            navigate(location.pathname, { replace: true, state: {} });
        });
    }, [location.state, location.pathname, applySiteSelection, navigate, loadedSiteGuid, phase]);

    useEffect(() => {
        if (!bootCompleteRef.current) return;
        if (location.state?.openSitePicker !== true) return;
        setPhase("select");
        setPickerSiteGuid("");
        setLoading(false);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    const handleHeaderSiteChange = useCallback((siteGuid) => {
        if (!siteGuid) return;
        applySiteSelection(siteGuid, { keepSelectedBin: false });
    }, [applySiteSelection]);

    const handleContinueFromPicker = useCallback((guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next, { keepSelectedBin: false });
    }, [pickerSiteGuid, applySiteSelection]);

    // Mark that we're in Bin3D (single-bin view) - prevents highlightStice from calling siteView
    useEffect(() => {
        if (global.gw.selectedSite) {
            global.gw.selectedSite.isBinWindowView = true;
        }
        return () => {
            if (global.gw.selectedSite) {
                global.gw.selectedSite.isBinWindowView = false;
            }
        };
    }, [loadedSiteGuid]);

    // Reset cable selection when bin changes
    useEffect(() => {
        const handleBinChange = () => {
            // Reset cable selection to "Select Cable" when bin changes
            setSelectedCableId("select-cable");
            
            // Clear cable focus flags
            if (!global.gw.selectedSite) return;
            global.gw.selectedSite.isCableFocused = false;
            global.gw.selectedSite.focusedCableId = null;
            if (global.gw.selectedSite.triggerGradesRerender) global.gw.selectedSite.triggerGradesRerender();
        };
        
        // Store the handler in global so it can be called from other components
        if (global.gw.selectedSite) {
            global.gw.selectedSite.onBinChange = handleBinChange;
        }
        
        return () => {
            if (global.gw.selectedSite) {
                global.gw.selectedSite.onBinChange = null;
            }
        };
    }, [loadedSiteGuid]);

    // Re-fetch the latest BinLevel snapshot for the bin currently focused in the dropdown.
    // Runs both on mount (covers the case where setSelectedBinId already matches gw.selectedSite.selectedBinId)
    // and whenever the user picks a different bin in the dropdown.
    useEffect(() => {
        if (!selectedBinId) return;
        global.setSiteBinLevels(selectedBinId);
    }, [selectedBinId]);

    const renderBins = () => {
        return ((global.gw.selectedSite?.bins || []).map((b  , i) => (
            <MuiMaterials.MenuItem 
                key={`binSelect-${i}`} 
                value={b.binDataId != null ? String(b.binDataId) : String(b.binId ?? i)} 
                sx={{ 
                    fontSize: "14px", 
                    fontWeight: "700" 
                }}
            >
                {b.binName}
            </MuiMaterials.MenuItem>
        )));
    };

    const siteGuid = loadedSiteGuid || pickerSiteGuid || global.gw.selectedSite?.siteGuid || null;
    const headerSelectSx = {
        minWidth: 128,
        maxWidth: 220,
        "& .MuiInputBase-root": {
            height: 36,
            fontSize: 13,
            fontWeight: 600,
        },
        "& .MuiSelect-select": {
            py: 0.75,
            px: 1.25,
        },
    };
    const headerSelectMenuProps = {
        MenuProps: withScrollHintMenuProps({
            PaperProps: {
                className: "hide-scrollbar",
                sx: {
                    backgroundColor: getThemeColor("#ffffff", "#2d2d2d"),
                    "& .MuiMenuItem-root": {
                        color: getThemeColor("#333333", "#f4cc8e"),
                        backgroundColor: "transparent",
                        "&:hover": {
                            backgroundColor: getThemeColor("#f5f5f5", "#3d3d3d"),
                        },
                    },
                    "& .MuiList-root": {
                        backgroundColor: getThemeColor("#ffffff", "#2d2d2d"),
                    },
                },
            },
        }),
    };

    const loadingOverlay = isLoading ? (
        <MuiMaterials.Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: getThemeColor("rgba(255,255,255,0.65)", "rgba(0,0,0,0.55)"),
            }}
        >
            <MuiMaterials.RotatingLines
                strokeColor={getThemeColor("#333333", "#f4cc8e")}
                strokeWidth="3"
                animationDuration="0.72"
                width="48"
                visible
            />
        </MuiMaterials.Box>
    ) : null;

    return (
        <WindowChrome
            className="binWindow hide-scrollbar"
            title="Bin 3D"
            toolTitle="Bin 3D"
            siteWindowId={SITE_WINDOW_IDS.bin}
            siteGuid={siteGuid}
            showHeaderSiteSelect={phase === "view"}
            onHeaderSiteChange={handleHeaderSiteChange}
            headerFilterSite={(s) => !s.isHms}
            headerSiteSelectDisabled={isLoading || phase !== "view"}
            onClose={() => navigate("/sites", { replace: true })}
            loadingOverlay={loadingOverlay}
            actions={
                phase === "view" && !isLoading ? (
                    <>
                        <MuiMaterials.TextField
                            select
                            size="small"
                            value={selectedBinId}
                            label="Bin"
                            variant="outlined"
                            SelectProps={headerSelectMenuProps}
                            sx={headerSelectSx}
                            onChange={async (e) => {
                                         // Clear cable focus when bin changes
                                         global.gw.selectedSite.isCableFocused = false;
                                         global.gw.selectedSite.focusedCableId = null;
                                         if (global.gw.selectedSite.triggerGradesRerender) global.gw.selectedSite.triggerGradesRerender();
                                         setSelectedCableId("select-cable");
                                         
                                         // Restore opacity of all cables in the previously selected bin
                                         if (global.gw.selectedSite.selectedBinId) {
                                             const previousBin = global.gw.selectedSite.binsRef.find(bin => 
                                                 bin.binDataId === parseInt(global.gw.selectedSite.selectedBinId)
                                             );
                                             if (previousBin) {
                                                 global.gw.selectedSite.cablesRef.forEach(cable => {
                                                     // Compare using the sequential bin ID
                                                     if (cable.binId === previousBin.binId) {
                                                         cable.meshes.forEach(mesh => {
                                                             if (mesh.material && !mesh.isText) { // Skip text sprites
                                                                 mesh.material.opacity = 1.0;
                                                             }
                                                        });
                                                     }
                                                 });
                                             }
                                         }
                                         
                                         global.toggleCableLevelRef();
                                         global.renderGrainLevels();

                                         const gli = window.document.getElementsByClassName("gli")[0];
                                         if (gli) {
                                             gli.parentElement.style.color = global.gw.theme === 'light' ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                         }

                                         const bti = window.document.getElementsByClassName("bti")[0];
                                         if (bti) {
                                             bti.parentElement.style.color = global.gw.theme === 'light' ? "rgba(51, 51, 51, 0.38)" : "rgba(244, 204, 142, 0.38)";
                                         }

                                         global.gw.selectedSite.showBinThermo = false;
                                         global.gw.selectedSite.showGrainLevels = false;
                                        
                                        global.gw.selectedSite.binsRef.map(b => {
                                            try {
                                                for (let i = 0; i < b.parent.parent.parent.children.length; i++) {
                                                    let child = b.parent.parent.parent.children[i];
                                                    if (child.isParticle) {
                                                        b.parent.parent.parent.remove(child);
                                                        i--;
                                                    }
                                                }
                                            } catch (error) {
                                                
                                            }
                                        });

                                         // Center the new bin's footprint (and its cables) before the canvas re-renders
                                         global.focusBinViewOnBin(e.target.value);
                                         setSelectedBinId(String(e.target.value));
                                         global.gw.selectedSite.selectedBinId = String(e.target.value);
                                         global.gw.selectedSite.selectedBinName = global.gw.selectedSite.bins.find(b => b.binDataId == e.target.value).binName;
                                         
                                         // Call the bin change handler to reset cable selection
                                         if (global.gw.selectedSite.onBinChange) {
                                             global.gw.selectedSite.onBinChange();
                                         }
                                    }}
                        >
                            {renderBins()}
                        </MuiMaterials.TextField>
                        <MuiMaterials.TextField
                            select
                            size="small"
                            value={selectedCableId}
                            label="Cable"
                            variant="outlined"
                            SelectProps={headerSelectMenuProps}
                            sx={headerSelectSx}
                            onChange={(e) => {
                                        const layoutCableId = e.target.value;
                                        
                                        // If selecting "Select Cable", restore all cable visibility
                                        if (layoutCableId === "select-cable") {
                                            global.gw.selectedSite.isCableFocused = false;
                                            global.gw.selectedSite.focusedCableId = null;
                                            if (global.gw.selectedSite.triggerGradesRerender) global.gw.selectedSite.triggerGradesRerender();
                                            setSelectedCableId("select-cable");
                                            
                                            // Restore visibility of all cables in the current bin using global function
                                            if (global.gw.selectedSite.selectedBinId) {
                                                global.restoreCableVisibility(global.gw.selectedSite.selectedBinId);
                                            }
                                            
                                            // Update cable visibility state
                                            global.gw.selectedSite.isCablesOff = false;
                                            
                                            // Update the cable toggle button visual state to show "on"
                                            let tvcs = window.document.getElementsByClassName("cbi");
                                            if (tvcs && tvcs.length > 0) {
                                                tvcs[0].style.color = global.gw.theme === 'light' ? "rgb(51, 51, 51)" : "rgb(244, 204, 142)";
                                            }
                                            
                                            // Go back to the original bin view
                                            if (global.gw.selectedSite.orbitControls) {
                                                const selectedBin = global.gw.selectedSite.binsRef.find(bin => 
                                                    bin.binDataId === parseInt(global.gw.selectedSite.selectedBinId)
                                                );
                                                if (selectedBin) {
                                                    global.topView(selectedBin.binId);
                                                }
                                            }
                                            return;
                                        }
                                        
                                        setSelectedCableId(layoutCableId);
                                        
                                        if (layoutCableId) {
                                            // Find the selected layoutCable to get the actual cableId
                                            const selectedLayoutCable = global.gw.selectedSite.layoutCables.find(layoutCable => 
                                                layoutCable.customerSiteLayoutCableId === parseInt(layoutCableId)
                                            );
                                            
                                            if (selectedLayoutCable) {
                                                // Use the global function to focus on the cable (same as Site3d)
                                                global.focusOnCable(selectedLayoutCable.cableId);
                                            }
                                        }
                                    }}
                        >
                            <MuiMaterials.MenuItem value="select-cable">
                                <em>Select Cable</em>
                            </MuiMaterials.MenuItem>
                            {global.gw.selectedSite?.layoutCables && global.gw.selectedSite.layoutCables
                                .filter(layoutCable => {
                                    if (!global.gw.selectedSite.selectedBinId) return false;
                                    return layoutCable.binId === parseInt(global.gw.selectedSite.selectedBinId);
                                })
                                .map((layoutCable) => (
                                <MuiMaterials.MenuItem
                                    key={layoutCable.customerSiteLayoutCableId}
                                    value={layoutCable.customerSiteLayoutCableId}
                                >
                                    {cableNumberLabel(layoutCable)}
                                </MuiMaterials.MenuItem>
                            ))}
                        </MuiMaterials.TextField>
                    </>
                ) : null
            }
        >
            {phase === "select" ? (
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={handleContinueFromPicker}
                    onCancel={() => navigate("/sites", { replace: true })}
                    loading={isLoading}
                    filterSite={(s) => !s.isHms}
                />
            ) : null}
            {phase === "view" && !isLoading ? (
                <MuiMaterials.Box
                    key={`bin-3d-container-${global.gw.theme}-${binViewKey}`}
                    className="dragResize"
                    sx={{
                        overflow: "hidden",
                        textAlign: "center",
                        background: getThemeColor("#ffffff", "#2d2d2d"),
                        display: "flex",
                        justifyContent: "center",
                        alignContent: "center",
                        alignItems: "stretch",
                        width: "100%",
                        height: "100%",
                        color: getThemeColor("#333333", "#ffc04a"),
                    }}
                >
                    <div style={{ display: "contents" }}>
                        <Bin3d
                            key={`bin3d-${loadedSiteGuid}-${binViewKey}`}
                            isHistoryLoading={isHistoryLoading}
                            selectedBinId={selectedBinId}
                        />
                    </div>
                </MuiMaterials.Box>
            ) : null}
        </WindowChrome>
    );
}
export default BinWindow;
